/**
 * Matter Economics - API thunks (tag matter-economics).
 *
 * Thin thunks over the `apis` axios instance for the billing-rate and
 * rate-override endpoints documented in MATTER_ECONOMICS_API.md. They return the
 * parsed data so callers can hold it in local component state (these data sets
 * are feature-local and don't need global Redux state). Mutations surface a
 * success/error toast via showFeedbackModal.
 *
 * Wire conventions: money is decimal major units; currency is lowercase ISO 4217.
 */
import apis from '../../utils/apis';
import { AppDispatch } from '../index';
import { showFeedbackModal } from './UserFeedbackActions';
import { setOrgDefaultCurrency } from '../slices/orgDefaultCurrencySlice';
import type {
  ActivityType,
  FirmEconomics,
  FirmEconomicsFilters,
  MatterEconomicsSummary,
  MatterOutcome,
  MyTimeEntry,
  OrgUserBillingRateRow,
  OrgUserBillingRateWrite,
  OutcomeTaxonomyEntry,
  OutcomeWritePayload,
  RateOverride,
  TimeEntry,
  UserBillingRate,
} from '../../types/economics';
import { ACTIVITY_TYPE_FALLBACK } from '../../components/Matters/economics/economicsConstants';
import { translate } from '../../i18n/translate';
import { readErrorMessage } from '../../utils/errorMessage';

/** Unwrap the platform envelope and coerce to a list regardless of wrapper key. */
const toList = <T,>(responseData: any): T[] => {
  const d = responseData?.data ?? responseData;
  if (Array.isArray(d)) return d as T[];
  if (!d || typeof d !== 'object') return [];
  // Tolerate a wrapping key (e.g. { rate_overrides: [...] }) without guessing one.
  const firstArray = Object.values(d).find((v) => Array.isArray(v));
  return (firstArray as T[]) || [];
};

/** Unwrap the platform envelope for a single object. */
const toObject = <T,>(responseData: any): T => (responseData?.data ?? responseData) as T;

// ── Per-matter rate overrides (US #2236, task #2257) ──────────────────────────

export const fetchRateOverrides =
  (matterUuid: string) =>
  async (): Promise<RateOverride[]> => {
    const res = await apis.get(`/matters/${matterUuid}/rate-overrides`);
    return toList<RateOverride>(res.data);
  };

export const createRateOverride =
  (matterUuid: string, payload: { user_uuid: string; rate: number; currency: string; notes?: string }) =>
  async (dispatch: AppDispatch): Promise<RateOverride | null> => {
    try {
      const res = await apis.post(`/matters/${matterUuid}/rate-overrides`, payload);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Rate override added'), duration: 3000 }));
      surfaceWarnings(dispatch, res.data);
      return toObject<RateOverride>(res.data);
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: beError(error, 'Failed to add rate override'), duration: 4000 }));
      throw error;
    }
  };

export const updateRateOverride =
  (matterUuid: string, overrideId: number, payload: { rate?: number; currency?: string; notes?: string }) =>
  async (dispatch: AppDispatch): Promise<RateOverride | null> => {
    try {
      const res = await apis.patch(`/matters/${matterUuid}/rate-overrides/${overrideId}`, payload);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Rate override updated'), duration: 3000 }));
      return toObject<RateOverride>(res.data);
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: translate('Failed to update rate override'), duration: 4000 }));
      throw error;
    }
  };

export const deleteRateOverride =
  (matterUuid: string, overrideId: number) =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      await apis.delete(`/matters/${matterUuid}/rate-overrides/${overrideId}`);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Rate override removed'), duration: 3000 }));
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: translate('Failed to remove rate override'), duration: 4000 }));
      throw error;
    }
  };

// ── User default billing rates (US #2236, task #2258) ─────────────────────────

export const fetchUserBillingRates =
  (userId: number | string) =>
  async (): Promise<UserBillingRate[]> => {
    const res = await apis.get(`/users/${userId}/billing-rates`);
    return toList<UserBillingRate>(res.data);
  };

export interface UserBillingRateWritePayload {
  default_rate?: number;
  currency?: string;
  effective_from?: string;
  effective_to?: string | null;
}

/** Surface any BE `warnings: string[]` (envelope or data level) as info toasts. */
const surfaceWarnings = (dispatch: AppDispatch, responseData: any): void => {
  const warnings: unknown = responseData?.warnings ?? responseData?.data?.warnings;
  if (Array.isArray(warnings)) {
    warnings.forEach((w) =>
      dispatch(showFeedbackModal({ showModal: true, modalType: 'warning', message: String(w), duration: 5000 })),
    );
  }
};

/** Pull the BE's human message off an axios error envelope, falling back to a default. */
const beError = (error: any, fallback: string): string =>
  readErrorMessage(error, fallback);

export const createUserBillingRate =
  (userId: number | string, payload: UserBillingRateWritePayload) =>
  async (dispatch: AppDispatch): Promise<UserBillingRate | null> => {
    try {
      const res = await apis.post(`/users/${userId}/billing-rates`, payload);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Billing rate saved'), duration: 3000 }));
      surfaceWarnings(dispatch, res.data);
      return toObject<UserBillingRate>(res.data);
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: beError(error, 'Failed to save billing rate'), duration: 5000 }));
      throw error;
    }
  };

/** Edit an existing rate period (US #2236). PATCH /users/<id>/billing-rates/<rate_id>. */
export const updateUserBillingRate =
  (userId: number | string, rateId: number | string, payload: UserBillingRateWritePayload) =>
  async (dispatch: AppDispatch): Promise<UserBillingRate | null> => {
    try {
      const res = await apis.patch(`/users/${userId}/billing-rates/${rateId}`, payload);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Billing rate updated'), duration: 3000 }));
      surfaceWarnings(dispatch, res.data);
      return toObject<UserBillingRate>(res.data);
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: beError(error, 'Failed to update billing rate'), duration: 5000 }));
      throw error;
    }
  };

// ── Firm-admin billing-rates roster + batch (US #2236) ────────────────────────

/** Firm-admin roster: every active member's current rate + history count. */
export const fetchOrgUsersBillingRates =
  () =>
  async (): Promise<OrgUserBillingRateRow[]> => {
    const res = await apis.get('/organizations/me/users-billing-rates');
    return toList<OrgUserBillingRateRow>(res.data);
  };

/**
 * Firm-admin batch upsert (US #2236). PATCH /organizations/me/users-billing-rates
 * with only the changed rows. Atomic server-side: on a 422 nothing is written and
 * the per-row details are thrown to the caller. Surfaces BE warnings as toasts.
 */
export const saveOrgUsersBillingRates =
  (rows: OrgUserBillingRateWrite[]) =>
  async (dispatch: AppDispatch): Promise<{ updated: string[]; warnings: string[] }> => {
    try {
      const res = await apis.patch('/organizations/me/users-billing-rates', rows);
      const data = res.data ?? {};
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Billing rates updated'), duration: 3000 }));
      surfaceWarnings(dispatch, res.data);
      return { updated: data.updated || [], warnings: data.warnings || [] };
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: beError(error, 'Failed to update billing rates'), duration: 5000 }));
      throw error;
    }
  };

// ── Activity types (static dropdown, US #2237 / #2261) ────────────────────────

/**
 * Fetch activity types. Endpoint route is pending BE confirmation (see T1 in
 * MATTER_ECONOMICS_API.md); falls back to the 8 seeded types so the picker works.
 */
export const fetchActivityTypes =
  () =>
  async (): Promise<ActivityType[]> => {
    try {
      const res = await apis.get('/dropdown/activity-types');
      const list = toList<ActivityType>(res.data);
      return list.length ? list : ACTIVITY_TYPE_FALLBACK;
    } catch {
      return ACTIVITY_TYPE_FALLBACK;
    }
  };

// ── Time entries (US #2237, tasks #2265–#2268) ────────────────────────────────

export interface TimeEntryFilters {
  user_id?: number | string;
  date_from?: string;
  date_to?: string;
  activity_type_id?: number | string;
  billable?: boolean;
  matter_id?: number | string;
}

export interface TimeEntryWritePayload {
  entry_date: string;
  hours_decimal: number;
  activity_type_id?: number | null;
  billable: boolean;
  description?: string;
  /**
   * Whose entry this is (uuid-first write, see MATTER_ECONOMICS_API.md). Defaults
   * to the caller server-side; owner/admin may set others. Required on create.
   */
  user_uuid?: string;
}

/** Drop empty/undefined filter values so we don't send blank query params. */
const cleanParams = (filters: Record<string, any>): Record<string, any> =>
  Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== '' && v !== null));

export const fetchTimeEntries =
  (matterUuid: string, filters: TimeEntryFilters = {}) =>
  async (): Promise<TimeEntry[]> => {
    const res = await apis.get(`/matters/${matterUuid}/time-entries`, { params: cleanParams(filters) });
    return toList<TimeEntry>(res.data);
  };

export const createTimeEntry =
  (matterUuid: string, payload: TimeEntryWritePayload) =>
  async (dispatch: AppDispatch): Promise<TimeEntry | null> => {
    try {
      const res = await apis.post(`/matters/${matterUuid}/time-entries`, payload);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Time entry added'), duration: 3000 }));
      surfaceWarnings(dispatch, res.data);
      return toObject<TimeEntry>(res.data);
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: beError(error, 'Failed to add time entry'), duration: 4000 }));
      throw error;
    }
  };

export const updateTimeEntry =
  (matterUuid: string, entryId: number, payload: Partial<TimeEntryWritePayload>) =>
  async (dispatch: AppDispatch): Promise<TimeEntry | null> => {
    try {
      const res = await apis.patch(`/matters/${matterUuid}/time-entries/${entryId}`, payload);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Time entry updated'), duration: 3000 }));
      surfaceWarnings(dispatch, res.data);
      return toObject<TimeEntry>(res.data);
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: beError(error, 'Failed to update time entry'), duration: 4000 }));
      throw error;
    }
  };

export const deleteTimeEntry =
  (matterUuid: string, entryId: number) =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      await apis.delete(`/matters/${matterUuid}/time-entries/${entryId}`);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Time entry removed'), duration: 3000 }));
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: translate('Failed to remove time entry'), duration: 4000 }));
      throw error;
    }
  };

/**
 * Global "my time entries" across matters (task #2268). Route pending BE
 * confirmation (T2 in MATTER_ECONOMICS_API.md); returns [] on failure so the page
 * can render an empty/error state.
 */
export const fetchMyTimeEntries =
  (userId: number | string, filters: TimeEntryFilters = {}) =>
  async (): Promise<MyTimeEntry[]> => {
    const res = await apis.get(`/users/${userId}/time-entries`, { params: cleanParams(filters) });
    return toList<MyTimeEntry>(res.data);
  };

// ── Per-matter economics (US #2238, tasks #2272–#2275) ────────────────────────

export const fetchMatterEconomics =
  (matterUuid: string) =>
  async (): Promise<MatterEconomicsSummary> => {
    const res = await apis.get(`/matters/${matterUuid}/economics`);
    return toObject<MatterEconomicsSummary>(res.data);
  };

// ── Firm economics dashboard (US #2239, tasks #2278–#2281) ────────────────────

/** Flatten multi-value filters into repeatable query params. */
const firmParams = (filters: FirmEconomicsFilters): Record<string, any> =>
  cleanParams({
    date_from: filters.date_from,
    date_to: filters.date_to,
    currency: filters.currency,
    client_id: filters.client_id?.length ? filters.client_id : undefined,
    matter_type_id: filters.matter_type_id?.length ? filters.matter_type_id : undefined,
    user_id: filters.user_id?.length ? filters.user_id : undefined,
  });

export const fetchFirmEconomics =
  (filters: FirmEconomicsFilters = {}) =>
  async (): Promise<FirmEconomics> => {
    // Per MATTER_ECONOMICS_API.md (T3.1): the path segment is ignored; the BE
    // scopes to the authenticated org, so we always use the `me` alias.
    const res = await apis.get(`/organizations/me/economics`, {
      params: firmParams(filters),
      paramsSerializer: (params: Record<string, any>) => {
        // Repeat array params: ?client_id=1&client_id=2
        const parts: string[] = [];
        Object.entries(params).forEach(([k, v]) => {
          if (Array.isArray(v)) v.forEach((item) => parts.push(`${k}=${encodeURIComponent(item)}`));
          else if (v !== undefined) parts.push(`${k}=${encodeURIComponent(v)}`);
        });
        return parts.join('&');
      },
    });
    return toObject<FirmEconomics>(res.data);
  };

// ── Org default currency (#2246) ──────────────────────────────────────────────

const readCurrency = (responseData: any): string | null => {
  const d = responseData ?? {};
  return (d.default_currency ?? d.data?.default_currency ?? null) as string | null;
};

/** GET the org default currency (lowercase ISO 4217 | null) and cache it. */
export const fetchOrgDefaultCurrency =
  () =>
  async (dispatch: AppDispatch): Promise<string | null> => {
    try {
      const res = await apis.get('/organizations/me/default-currency');
      const cur = readCurrency(res.data);
      dispatch(setOrgDefaultCurrency(cur));
      return cur;
    } catch (error) {
      // Non-critical - mark loaded with null so consumers fall back to 'usd'.
      dispatch(setOrgDefaultCurrency(null));
      return null;
    }
  };

/** PUT the org default currency (firm admin only). Sends lowercase ISO 4217. */
export const updateOrgDefaultCurrency =
  (currency: string) =>
  async (dispatch: AppDispatch): Promise<string | null> => {
    try {
      const res = await apis.put('/organizations/me/default-currency', {
        default_currency: (currency || '').toLowerCase(),
      });
      const cur = readCurrency(res.data) ?? (currency || '').toLowerCase();
      dispatch(setOrgDefaultCurrency(cur));
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Organization default currency updated'), duration: 3000 }));
      surfaceWarnings(dispatch, res.data);
      return cur;
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: beError(error, 'Failed to update default currency'), duration: 4000 }));
      throw error;
    }
  };

// ── Matter outcome + taxonomy (US #2240 / #2241) ──────────────────────────────

/** GET the captured outcome for a matter (null until one is recorded). */
export const fetchMatterOutcome =
  (matterUuid: string) =>
  async (): Promise<MatterOutcome | null> => {
    const res = await apis.get(`/matters/${matterUuid}/outcome`);
    return (toObject<MatterOutcome | null>(res.data) as MatterOutcome | null) ?? null;
  };

/**
 * PUT (upsert) a matter's outcome WITHOUT changing status (the firm-admin "Edit
 * outcome" affordance). Owner/firm-admin only server-side. Audit-logged.
 */
export const upsertMatterOutcome =
  (matterUuid: string, payload: OutcomeWritePayload) =>
  async (dispatch: AppDispatch): Promise<MatterOutcome | null> => {
    try {
      const res = await apis.put(`/matters/${matterUuid}/outcome`, payload);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Outcome updated'), duration: 3000 }));
      surfaceWarnings(dispatch, res.data);
      return toObject<MatterOutcome>(res.data);
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: beError(error, 'Failed to update outcome'), duration: 4000 }));
      throw error;
    }
  };

/**
 * POST the combined close: atomically record the outcome + flip the matter to
 * closed. Owner/firm-admin only server-side; 422 if the outcome is missing.
 */
export const closeMatterWithOutcome =
  (matterUuid: string, payload: OutcomeWritePayload) =>
  async (dispatch: AppDispatch): Promise<any> => {
    try {
      const res = await apis.post(`/matters/${matterUuid}/close`, payload);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Matter closed'), duration: 3000 }));
      surfaceWarnings(dispatch, res.data);
      return toObject<any>(res.data);
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: beError(error, 'Failed to close matter'), duration: 4000 }));
      throw error;
    }
  };

/** GET the effective outcome taxonomy for the org (overrides + global defaults). */
export const fetchOutcomeTaxonomy =
  (includeInactive = false) =>
  async (): Promise<OutcomeTaxonomyEntry[]> => {
    const res = await apis.get('/organizations/me/outcome-taxonomy', {
      params: includeInactive ? { include_inactive: true } : undefined,
    });
    return toList<OutcomeTaxonomyEntry>(res.data);
  };

/** POST a new org outcome type (firm admin only). Reusing a default's name overrides it. */
export const createOutcomeTaxonomy =
  (payload: { name: string; label: string; sort_order?: number; is_active?: boolean }) =>
  async (dispatch: AppDispatch): Promise<OutcomeTaxonomyEntry | null> => {
    try {
      const res = await apis.post('/organizations/me/outcome-taxonomy', payload);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Outcome type added'), duration: 3000 }));
      return toObject<OutcomeTaxonomyEntry>(res.data);
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: beError(error, 'Failed to add outcome type'), duration: 4000 }));
      throw error;
    }
  };

/** PATCH an org-owned outcome type (firm admin only). */
export const updateOutcomeTaxonomy =
  (taxonomyId: number, payload: { label?: string; sort_order?: number; is_active?: boolean }) =>
  async (dispatch: AppDispatch): Promise<OutcomeTaxonomyEntry | null> => {
    try {
      const res = await apis.patch(`/organizations/me/outcome-taxonomy/${taxonomyId}`, payload);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Outcome type updated'), duration: 3000 }));
      return toObject<OutcomeTaxonomyEntry>(res.data);
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: beError(error, 'Failed to update outcome type'), duration: 4000 }));
      throw error;
    }
  };

/** DELETE (disable) an org-owned outcome type (firm admin only). */
export const deleteOutcomeTaxonomy =
  (taxonomyId: number) =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      await apis.delete(`/organizations/me/outcome-taxonomy/${taxonomyId}`);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: translate('Outcome type disabled'), duration: 3000 }));
    } catch (error) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: beError(error, 'Failed to disable outcome type'), duration: 4000 }));
      throw error;
    }
  };
